import {
  keepPreviousData,
  mutationOptions,
  queryOptions,
} from '@tanstack/react-query';

import { API_MODULES } from '@lib/api/constants';
import { RegisterAccountInput } from '@lib/api/repository/accounts/accounts.repository';
import DIContainer from '@lib/dicontainer';
import { Account } from '@lib/types';
import { AdminPageFilters } from '@lib/types/admin-page';

import { MutationVariables } from './types';

export const getAccountsBaseQueryKey = () => [API_MODULES.ACCOUNTS];

export const getAccountsBaseMutationKey = getAccountsBaseQueryKey;

const usecase = DIContainer.getAccountsUseCase();

export const registerAccountOptions = () =>
  mutationOptions({
    mutationKey: [...getAccountsBaseMutationKey(), 'register'],
    mutationFn: (payload: RegisterAccountInput) => usecase.register(payload),
  });

export const getAccountsOptions = () =>
  queryOptions({
    queryKey: [...getAccountsBaseQueryKey()],
    queryFn: ({ signal }) => usecase.list(signal),
    placeholderData: keepPreviousData,
  });

export const createAccountOptions = () =>
  mutationOptions({
    mutationKey: [...getAccountsBaseMutationKey(), 'create'],
    mutationFn: ({ payload }: MutationVariables<void, Account>) =>
      usecase.create(payload),
  });

export const updateAccountOptions = () =>
  mutationOptions({
    mutationKey: [...getAccountsBaseMutationKey(), 'update'],
    mutationFn: ({ payload }: MutationVariables<void, Account>) =>
      usecase.update(payload),
  });

export const updateProfilePhotoOptions = () =>
  mutationOptions({
    mutationKey: [...getAccountsBaseMutationKey(), 'update', 'profilePhoto'],
    mutationFn: ({ payload }: MutationVariables<void, File>) =>
      usecase.updateProfilePhoto(payload),
  });

export interface ApiUpdateAccountPasswordPayload {
  currentPassword: string;
  newPassword: string;
}

export const updateAccountPasswordOptions = () =>
  mutationOptions({
    mutationKey: [...getAccountsBaseMutationKey(), 'update', 'password'],
    mutationFn: ({
      payload,
    }: MutationVariables<void, ApiUpdateAccountPasswordPayload>) =>
      usecase.updatePassword(payload.currentPassword, payload.newPassword),
  });

export const askAccountVerificationCodeOptions = () =>
  mutationOptions({
    mutationKey: [
      ...getAccountsBaseMutationKey(),
      'update',
      'verificationCode',
    ],
    mutationFn: () => usecase.askVerificationCode(),
  });

export interface ApiVerifyAccountPayload {
  code: string;
}

export const verifyAccountOptions = () =>
  mutationOptions({
    mutationKey: [...getAccountsBaseMutationKey(), 'verify'],
    mutationFn: ({
      payload,
    }: MutationVariables<void, ApiVerifyAccountPayload>) =>
      usecase.verify(payload.code),
  });

export const exportAccountDataOptions = () =>
  mutationOptions({
    mutationKey: [...getAccountsBaseMutationKey(), 'export'],
    mutationFn: () => usecase.exportData(),
  });

export const deleteAccountOptions = () =>
  mutationOptions({
    mutationKey: [...getAccountsBaseMutationKey(), 'delete'],
    mutationFn: ({ payload }: MutationVariables<void, string>) =>
      usecase.deleteAccount(payload),
  });

export const setAccountActiveOptions = () =>
  mutationOptions({
    mutationKey: [...getAccountsBaseMutationKey(), 'active'],
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      usecase.setActive(id, active),
  });

export const adminAccountsPageOptions = (filters: AdminPageFilters) =>
  queryOptions({
    queryKey: [...getAccountsBaseQueryKey(), 'admin-page', filters],
    queryFn: ({ signal }) => usecase.listAdminPage(filters, signal),
  });

export const setAccountCPFOptions = () =>
  mutationOptions({
    mutationKey: [...getAccountsBaseMutationKey(), 'cpf'],
    mutationFn: ({ payload }: MutationVariables<void, string>) =>
      usecase.setCPF(payload),
  });
